import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CancelFlowerSubscriptionInput,
  FlowerSubscription,
  FlowerSubscriptionActionResult,
  FlowerSubscriptionDestinationInput,
  FlowerSubscriptionFulfillmentPreview,
  FlowerSubscriptionIdInput,
  FlowerSubscriptionIndexFilters,
  FlowerSubscriptionIndexResponse,
  NotifyFlowerSubscriptionFulfillmentUnavailableInput,
  SkipFlowerSubscriptionInput,
  UpdateFlowerSubscriptionFulfillmentDateInput,
  UpdateFlowerSubscriptionFulfillmentInput,
  UpdateFlowerSubscriptionPaymentMethodInput,
  UpdateFlowerSubscriptionPersonalizationInput,
  UpdateFlowerSubscriptionRecipientInput,
  UpdateFlowerSubscriptionScheduleInput,
} from './flower-subscriptions.types'

/** The organization every subscription query key carries; the scoped key builders always set it. */
export interface FlowerSubscriptionKeyEntries {
  organizationId: OrganizationId
}

export interface FlowerSubscriptionIndexKeyEntries extends FlowerSubscriptionKeyEntries {
  filters?: FlowerSubscriptionIndexFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

export interface FlowerSubscriptionShowKeyEntries extends FlowerSubscriptionKeyEntries {
  flowerSubscriptionId?: FlowerSubscriptionIdInput
}

/** Calls that act on one subscription; the Laravel routes require both ids. */
export interface FlowerSubscriptionTarget {
  organizationId: number
  flowerSubscriptionId: FlowerSubscriptionIdInput
}

/** Query functions read only the key and the abort signal. */
type FlowerSubscriptionQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export async function getFlowerSubscriptions({
  queryKey: [{ organizationId, filters = {}, page = 1, pageSize = 20 }],
  signal,
}: FlowerSubscriptionQueryContext<FlowerSubscriptionIndexKeyEntries>) {
  return (
    (await axios.get<FlowerSubscriptionIndexResponse>(
      '/flower-subscriptions',
      buildAdminRequestConfig({
        organizationId,
        params: { ...filters, page, page_size: pageSize },
        ...(signal ? { signal } : {}),
      })
    )) || {
      data: [],
      meta: {
        current_page: page,
        page_size: pageSize,
        total: 0,
        last_page: 1,
      },
    }
  )
}

export async function getFlowerSubscription({
  queryKey: [{ organizationId, flowerSubscriptionId }],
  signal,
}: FlowerSubscriptionQueryContext<FlowerSubscriptionShowKeyEntries>) {
  const response = await axios.get<FlowerSubscription>(
    `/flower-subscriptions/${flowerSubscriptionId}`,
    buildAdminRequestConfig({
      organizationId,
      ...(signal ? { signal } : {}),
    })
  )

  return response || {}
}

export async function skipFlowerSubscription({
  organizationId,
  flowerSubscriptionId,
  notifyCustomer = false,
}: FlowerSubscriptionTarget & SkipFlowerSubscriptionInput) {
  return axios.post<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/skip`,
    { notify_customer: notifyCustomer },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function resumeSkippedFlowerSubscription({
  organizationId,
  flowerSubscriptionId,
}: FlowerSubscriptionTarget) {
  return axios.post<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/resume-skip`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function cancelFlowerSubscription({
  organizationId,
  flowerSubscriptionId,
  notifyCustomer = true,
}: FlowerSubscriptionTarget & CancelFlowerSubscriptionInput) {
  return axios.post<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/cancel`,
    {
      notify_customer: notifyCustomer,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateFlowerSubscriptionPaymentMethod({
  organizationId,
  flowerSubscriptionId,
  paymentMethodId,
}: FlowerSubscriptionTarget & UpdateFlowerSubscriptionPaymentMethodInput) {
  return axios.post<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/payment-method`,
    { payment_method_id: paymentMethodId },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateFlowerSubscriptionFulfillmentDate({
  organizationId,
  flowerSubscriptionId,
  fulfillmentDate,
  allowUnavailable = false,
}: FlowerSubscriptionTarget & UpdateFlowerSubscriptionFulfillmentDateInput) {
  return axios.post<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/fulfillment-date`,
    { fulfillment_date: fulfillmentDate, allow_unavailable: allowUnavailable },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateFlowerSubscriptionSchedule({
  organizationId,
  flowerSubscriptionId,
  frequency,
  preferredFulfillmentWeekday,
  nextFulfillmentDate,
  expectedFrequency,
  expectedPreferredFulfillmentWeekday,
  expectedNextFulfillmentDate,
  allowUnavailable = false,
  immediateChargeConfirmed = false,
}: FlowerSubscriptionTarget & UpdateFlowerSubscriptionScheduleInput) {
  return axios.patch<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/schedule`,
    {
      frequency,
      preferred_fulfillment_weekday: preferredFulfillmentWeekday,
      next_fulfillment_date: nextFulfillmentDate,
      expected_frequency: expectedFrequency,
      expected_preferred_fulfillment_weekday: expectedPreferredFulfillmentWeekday,
      expected_next_fulfillment_date: expectedNextFulfillmentDate,
      allow_unavailable: allowUnavailable,
      immediate_charge_confirmed: immediateChargeConfirmed,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function previewFlowerSubscriptionFulfillment({
  organizationId,
  flowerSubscriptionId,
  fulfillmentType,
  locationId,
  deliveryAddress,
}: FlowerSubscriptionTarget & FlowerSubscriptionDestinationInput) {
  return axios.post<FlowerSubscriptionFulfillmentPreview>(
    `/flower-subscriptions/${flowerSubscriptionId}/fulfillment/preview`,
    {
      fulfillment_type: fulfillmentType,
      location_id: locationId,
      delivery_address: deliveryAddress,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateFlowerSubscriptionFulfillment({
  organizationId,
  flowerSubscriptionId,
  fulfillmentType,
  locationId,
  deliveryAddress,
  nextFulfillmentDate,
  preferredFulfillmentWeekday,
  specialInstructions,
  expectedFulfillmentType,
  expectedLocationId,
  expectedDeliveryAddressId,
  expectedFrequency,
  expectedPreferredFulfillmentWeekday,
  expectedNextFulfillmentDate,
  expectedSpecialInstructions,
  allowUnavailable = false,
  immediateChargeConfirmed = false,
}: FlowerSubscriptionTarget & UpdateFlowerSubscriptionFulfillmentInput) {
  return axios.patch<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/fulfillment`,
    {
      fulfillment_type: fulfillmentType,
      location_id: locationId,
      delivery_address: deliveryAddress,
      next_fulfillment_date: nextFulfillmentDate,
      preferred_fulfillment_weekday: preferredFulfillmentWeekday,
      special_instructions: specialInstructions,
      expected_fulfillment_type: expectedFulfillmentType,
      expected_location_id: expectedLocationId,
      expected_delivery_address_id: expectedDeliveryAddressId,
      expected_frequency: expectedFrequency,
      expected_preferred_fulfillment_weekday: expectedPreferredFulfillmentWeekday,
      expected_next_fulfillment_date: expectedNextFulfillmentDate,
      expected_special_instructions: expectedSpecialInstructions,
      allow_unavailable: allowUnavailable,
      immediate_charge_confirmed: immediateChargeConfirmed,
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function notifyFlowerSubscriptionFulfillmentUnavailable({
  organizationId,
  flowerSubscriptionId,
  message,
}: FlowerSubscriptionTarget & NotifyFlowerSubscriptionFulfillmentUnavailableInput) {
  return axios.post<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/notify-fulfillment-unavailable`,
    { ...(message !== undefined ? { message } : {}) },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateFlowerSubscriptionPersonalization({
  organizationId,
  flowerSubscriptionId,
  cardMessage,
  signature,
  specialInstructions,
}: FlowerSubscriptionTarget & UpdateFlowerSubscriptionPersonalizationInput) {
  return axios.patch<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/personalization`,
    {
      ...(cardMessage !== undefined ? { card_message: cardMessage } : {}),
      ...(signature !== undefined ? { signature } : {}),
      ...(specialInstructions !== undefined ? { special_instructions: specialInstructions } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateFlowerSubscriptionRecipient({
  organizationId,
  flowerSubscriptionId,
  recipientName,
  recipientPhone,
  recipientEmail,
}: FlowerSubscriptionTarget & UpdateFlowerSubscriptionRecipientInput) {
  return axios.patch<FlowerSubscriptionActionResult>(
    `/flower-subscriptions/${flowerSubscriptionId}/recipient`,
    {
      recipient_name: recipientName,
      recipient_phone: recipientPhone,
      recipient_email: recipientEmail,
    },
    buildAdminRequestConfig({ organizationId })
  )
}
